import { ActiveSymbolsService, RawActiveSymbols, TProcessedSymbolItem, TCategorizedSymbols, TMarketStatusChanges } from './ActiveSymbolsService';
import { IMarketStatusService } from './IMarketStatusService';

export type FetchActiveSymbolsFn = () => Promise<RawActiveSymbols>;
export type GetMarketsOrderFn = (raw: RawActiveSymbols) => string[];

export interface ISymbolService {
  init(): Promise<void>;
  isInitialized(): boolean;
  getLocale(): string;
  setLocale(locale: string): Promise<void>; // triggers reload if locale changed
  getCategorizedSymbols(): TCategorizedSymbols;
  getProcessedSymbols(): TProcessedSymbolItem[];
  getSymbol(code: string): TProcessedSymbolItem | undefined;
  getDecimalPlaces(code: string): number | undefined;
  onMarketStatus(cb: (changes: TMarketStatusChanges) => void): () => void;
  applyMarketStatusChanges(changes: TMarketStatusChanges): void;
  refresh(force?: boolean): Promise<void>; // manual reload (e.g. invalidate cache)
  // Market status methods
  isMarketOpen(symbol: string): boolean | undefined;
  isFeedUnavailable(symbol: string): boolean | undefined;
  getDelayedMinutes(symbol: string): number | undefined;
  destroy(): void;
}

export interface SymbolServiceOptions {
  fetchActiveSymbols: FetchActiveSymbolsFn;
  getMarketsOrder?: GetMarketsOrderFn;
  locale?: string;
  cacheByLocale?: boolean; // default true
  marketStatusService?: IMarketStatusService; // optional; if provided, wires market status events
}

export class SymbolService implements ISymbolService {
  private _activeSymbolsService = new ActiveSymbolsService();
  private _opts: SymbolServiceOptions;
  private _locale: string;
  private _initialized = false;
  private _loadingPromise: Promise<void> | null = null;
  private _rawCache: Record<string, RawActiveSymbols> = {}; // locale -> raw symbols
  private _marketStatusService?: IMarketStatusService;
  private _marketStatusUnsubscribe?: () => void;

  constructor(opts: SymbolServiceOptions) {
    this._opts = { cacheByLocale: true, ...opts };
    this._locale = opts.locale || 'en';
    this._marketStatusService = opts.marketStatusService;
  }

  async init(): Promise<void> {
    if (this._initialized) return;
    await this._loadForLocale(this._locale);
    if (this._marketStatusService) {
      await this._marketStatusService.init();
      this._marketStatusUnsubscribe = this._marketStatusService.onMarketStatusChange(changes => {
        this._activeSymbolsService.applyMarketStatusChanges(changes);
      });
    }
    this._initialized = true;
  }

  isInitialized(): boolean { return this._initialized; }

  getLocale(): string { return this._locale; }

  async setLocale(locale: string): Promise<void> {
    if (locale === this._locale) return; // no change
    this._locale = locale;
    await this._loadForLocale(locale, true);
  }

  async refresh(force = false): Promise<void> {
    await this._loadForLocale(this._locale, force);
  }

  getCategorizedSymbols(): TCategorizedSymbols {
    return this._activeSymbolsService.getCategorizedSymbols();
  }
  getProcessedSymbols(): TProcessedSymbolItem[] {
    return this._activeSymbolsService.getProcessedSymbols();
  }
  getSymbol(code: string): TProcessedSymbolItem | undefined {
    return this._activeSymbolsService.getSymbol(code);
  }
  getDecimalPlaces(code: string): number | undefined {
    return this.getSymbol(code)?.decimal_places;
  }
  onMarketStatus(cb: (changes: TMarketStatusChanges) => void): () => void {
    return this._activeSymbolsService.onMarketStatusChange(cb);
  }
  applyMarketStatusChanges(changes: TMarketStatusChanges): void {
    this._activeSymbolsService.applyMarketStatusChanges(changes);
  }
  isMarketOpen(symbol: string): boolean | undefined {
    return this._marketStatusService?.isMarketOpen(symbol);
  }
  isFeedUnavailable(symbol: string): boolean | undefined {
    return this._marketStatusService?.isFeedUnavailable(symbol);
  }
  getDelayedMinutes(symbol: string): number | undefined {
    return this._marketStatusService?.getDelayedMinutes(symbol);
  }
  destroy(): void {
    if (this._marketStatusUnsubscribe) {
      this._marketStatusUnsubscribe();
      this._marketStatusUnsubscribe = undefined;
    }
    this._marketStatusService?.destroy();
  }

  private async _loadForLocale(locale: string, force = false): Promise<void> {
    if (this._loadingPromise) {
      await this._loadingPromise; // serialize loads
    }
    const doLoad = async () => {
      let raw: RawActiveSymbols | undefined = undefined;
      const useCache = this._opts.cacheByLocale !== false && this._rawCache[locale] && !force;
      if (useCache) {
        raw = this._rawCache[locale];
      } else {
        raw = await this._opts.fetchActiveSymbols();
        if (this._opts.cacheByLocale !== false) {
          this._rawCache[locale] = raw;
        }
      }
      const marketOrder = this._opts.getMarketsOrder ? this._opts.getMarketsOrder(raw) : undefined;
      await this._activeSymbolsService.load(raw, true, marketOrder); // force load to apply new order / data
    };
    this._loadingPromise = doLoad();
    try {
      await this._loadingPromise;
    } finally {
      this._loadingPromise = null;
    }
  }
}

export default SymbolService;